import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
    CellType,
    IChipClickEventArgs,
    IDropDroppedEventArgs,
    IGridCellEventArgs,
    IgxDialogComponent,
    IgxGridComponent,
    IgxListItemComponent,
    IgxRowDirective,
    IgxToggleDirective,
    ISimpleComboSelectionChangingEventArgs
} from '@infragistics/igniteui-angular';
import { ColorPickerControl } from '@iplab/ngx-color-picker';
import { Editor } from 'ngx-editor';
import { Bus, BusForm } from 'src/app/models/bus';
import { BusStop, BusStopForm } from 'src/app/models/bus-stop';
import { BusStopSchema, BusStopSchemaForm } from 'src/app/models/bus-stop-schema';
import { DefaultValue, DefaultValueForm } from 'src/app/models/default-value';
import { Level, LevelForm } from 'src/app/models/level';
import { MailContext } from 'src/app/models/mail-context';
import { PaymentSource, PaymentSourceForm } from 'src/app/models/payment-source';
import { Sport, SportForm } from 'src/app/models/sport';
import { Template, TemplateForm } from 'src/app/models/template';
import { TransportSchema, TransportSchemaForm } from 'src/app/models/transport-schema';
import { User } from 'src/app/models/user';
import { MessagesService } from 'src/app/services/messages.service';
import { SettingsService } from 'src/app/services/settings.service';
import { WaitIndicatorService } from 'src/app/services/wait-indicator.service';
import { updateCollection } from 'src/app/shared/utils';

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss']
})
export class SettingsComponent implements OnInit, OnDestroy {
    @ViewChild('levelsGrid', { static: true, read: IgxGridComponent })
    private levelsGrid?: IgxGridComponent;
    protected githubControl = new ColorPickerControl().setValueFrom('red');
    protected dialogHeader: string = "Добави";

    public constructor(
        private msgs: MessagesService,
        private settingsService: SettingsService,
        private wait: WaitIndicatorService,
    ) {
        this.load();
    }

    ngOnInit(): void {
        this.editor = new Editor();
    }

    ngOnDestroy(): void {
        this.editor.destroy();
    }

    //#region Sport
    private editSport?: Sport;
    private selectedSport: Sport | undefined;
    protected sports: Sport[] = [];
    protected sportForm!: FormGroup<SportForm>;
    public emptyTemplateMessage = 'Изберете спорт';

    public openSportDialog = (dialog: IgxDialogComponent, cell?: CellType): void => {
        this.editSport = cell?.row?.data;
        this.sportForm.reset(this.editSport);
        this.dialogHeader = cell ? 'Редактирай' : 'Добави';
        dialog!.open();
    }
    public sportSubmit = (dialog: IgxDialogComponent): void => {
        this._saveSport(this.dialogHeader === 'Добави', dialog);
    }
    private _saveSport = async (add: boolean, dialog: IgxDialogComponent) => {
        try {
            let sport: Sport;
            if (add) {
                sport = await this.settingsService.sportAdd(this.sportForm.value);
            } else {
                sport = await this.settingsService.sportUpdate({ ...this.sportForm.value, id: this.editSport?.id });
            }
            this.sports = updateCollection(sport, this.sports);
            this.msgs.success(`Спорт ${sport.name} е успешно ${add ? 'добавен' : 'редактиран'}.`);
            this.editSport = undefined;
            this.sportForm.reset();
            dialog.close();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
    protected sportSelected = async (e: IGridCellEventArgs) => {
        this.emptyTemplateMessage = `Добавете нива за ${e.cell.row.data.name}`;
        this.selectedSport = e.cell.row.data;
        this.selectedLevels = this.selectedSport?.levels ?? [];
        this.updateSportDefaultValue();
    }
    //#endregion Sport

    //#region Level
    private editLevel?: Level;
    protected levels: Level[] = [];
    protected selectedLevels: Level[] = [];
    protected levelForm!: FormGroup<LevelForm>;
    protected levelToDelete?: string;
    protected deleteDialogHeader: string = "";

    public openLevelDialog = (dialog: IgxDialogComponent, cell?: CellType): void => {
        this.editLevel = cell?.row?.data;
        this.editLevel!.sport = this.selectedSport!;
        this.editLevel!.public = false;
        this.levelForm.reset(this.editLevel);
        this.dialogHeader = cell ? 'Редактирай' : 'Добави';
        dialog!.open();
    }
    public levelSubmit = (dialog: IgxDialogComponent): void => {
        this._saveLevel(this.dialogHeader === 'Добави', dialog);
    }
    private _saveLevel = async (add: boolean, dialog: IgxDialogComponent) => {
        try {
            let level: Level;
            if (add) {
                level = await this.settingsService.levelAdd({ ...this.levelForm.value, sport: this.selectedSport?.id });
            } else {
                level = await this.settingsService.levelUpdate({ ...this.levelForm.value, id: this.editLevel?.id, sport: this.selectedSport?.id });
            }
            this.levels = updateCollection(level, this.levels);
            this.selectedLevels = updateCollection(level, this.selectedLevels);
            this.selectedSport!.levels = this.selectedLevels;
            this.msgs.success(`Ниво ${level.name} е успешно ${add ? 'добавено' : 'редактирано'}.`);
            this.editLevel = undefined;
            this.sportForm.reset();
            dialog.close();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
    public openDeleteLevelDialog = async (dialog: IgxDialogComponent, cell: CellType): Promise<void> => {
        this.levelToDelete = undefined;
        const id = cell.row.data.id;
        const canDelete = await this.settingsService.levelCanBeDeleted(id);
        if (canDelete) {
            this.deleteDialogHeader = `Потвърди изтриване на ниво ${cell.row.data.name}`;
            this.levelToDelete = id;
        } else {
            this.deleteDialogHeader = `Ниво ${cell.row.data.name} не може да бъде изтрито!`;
            this.levelToDelete = undefined;
        }
        dialog.open();
    }
    public deleteLevel = async (dialog: IgxDialogComponent): Promise<void> => {
        if (this.levelToDelete) {
            await this.settingsService.levelDelete(this.levelToDelete!);
            this.selectedLevels = this.selectedLevels.filter(l => l.id !== this.levelToDelete);
            this.levels = this.levels.filter(l => l.id !== this.levelToDelete);
        }
        this.levelToDelete = undefined;
        dialog.close();
    }
    public levelDropped = async (args: IDropDroppedEventArgs): Promise<void> => {
        if (!this.levelsGrid)
            return;

        const event = args.originalEvent;
        const current = this.selectedLevels.find(l => l.id === args.dragData.key);
        const over = this.getCurrentRowIndex(this.levelsGrid.rowList.toArray(), { x: event.clientX, y: event.clientY });
        if (!over || !current)
            return;

        if (current.timeCreate === over.timeCreate)
            return;

        const currentIndex = this.selectedLevels.indexOf(current);
        const overIndex = this.selectedLevels.indexOf(over);

        if (currentIndex > overIndex) {
            for (let i = overIndex; i < currentIndex; i++) {
                this.swapDates(this.selectedLevels[i], this.selectedLevels[i + 1]);
            }
        } else {
            for (let i = overIndex; i > currentIndex; i--) {
                this.swapDates(this.selectedLevels[i], this.selectedLevels[i - 1]);
            }
        }
        const levels = await this.settingsService.levelSetOrder(this.selectedLevels);
        this.selectedLevels = [...levels];
        this.msgs.success(`Редът на нивата е запазен.`);
    }
    private getCurrentRowIndex = (rowList: IgxRowDirective[], position: { x: number, y: number }): Level | undefined => {
        for (const row of rowList) {
            const rowRect = row.nativeElement.getBoundingClientRect();
            if (rowRect.top + window.scrollY < position.y && position.y < rowRect.bottom + window.scrollY &&
                rowRect.left + window.scrollX < position.x && position.x < rowRect.right + window.scrollX) {
                const found = this.selectedLevels.find(l => l.id === row.key);
                return found;
            }
        }
        return undefined;
    }
    private swapDates = (a: Level, b: Level): void => {
        const date = a.timeCreate;
        a.timeCreate = b.timeCreate;
        b.timeCreate = date;
    }
    //#endregion Level

    //#region Default value
    protected defaultValues: DefaultValue[] = [];
    protected editGlobalDefaultValue?: DefaultValue;
    protected defaultValuesForm!: FormGroup<DefaultValueForm>;

    public openDefaultValuesDialog(dialog: IgxDialogComponent) {
        this.dialogHeader = this.editGlobalDefaultValue ? 'Редактирай' : 'Добави';
        this.defaultValuesForm.reset(this.editGlobalDefaultValue);
        dialog.open();
    }
    public formatLabel = (value: number) => {
        return Math.round(value);
    }
    public async modifyDefaultValue(dialog: IgxDialogComponent) {
        try {
            if (this.dialogHeader === 'Добави') {
                this.editGlobalDefaultValue = await this.settingsService.defaultValueAdd(this.defaultValuesForm.value);
            } else {
                this.editGlobalDefaultValue = await this.settingsService.defaultValueUpdate({ ...this.defaultValuesForm.value, id: this.editGlobalDefaultValue?.id });
            }
            this.defaultValues = updateCollection(this.editGlobalDefaultValue, this.defaultValues);
            this.msgs.success(`Стойностите по подразбиране са обновени успешно.`);
        }
        catch (err) {
            this.msgs.error(err);
        }
        finally {
            dialog.close();
            this.defaultValuesForm.reset(undefined);
        }
    }
    private updateSportDefaultValue() {
        // TODO: move this to programs and set specific default value per program
        // this.sportDefaultValue = this.defaultValues.find(d => d.parentId && d.parentId === this.selectedSport?.id);
    }
    //#endregion Default value

    //#region Bus
    private editBus?: Bus;
    protected buses: Bus[] = [];
    protected busForm!: FormGroup<BusForm>;

    public openBusDialog = (dialog: IgxDialogComponent, cell?: CellType): void => {
        this.editBus = cell?.row?.data;
        this.dialogHeader = cell ? 'Редактирай' : 'Добави';
        this.busForm.reset({
            registrationNumber: this.editBus?.registrationNumber,
            color: this.editBus?.color,
            capacity: this.editBus?.capacity,
            driver: this.editBus?.driver?.id,
        });
        dialog.open();
    }
    public colorSelected = (color: string, toggle: IgxToggleDirective) => {
        this.busForm.controls.color.setValue(color);
        toggle.close();
    }
    public busSubmit = (dialog: IgxDialogComponent): void => {
        this._saveBus(this.dialogHeader === 'Добави', dialog);
    }
    private _saveBus = async (add: boolean, dialog: IgxDialogComponent) => {
        try {
            if (add) {
                this.editBus = await this.settingsService.busAdd(this.busForm.value);
            } else {
                this.editBus = await this.settingsService.busUpdate({ ...this.busForm.value, id: this.editBus?.id });
            }
            this.buses = updateCollection(this.editBus, this.buses);
            this.msgs.success(`Бус ${this.editBus.registrationNumber} е успешно ${add ? 'добавен' : 'редактиран'}.`);
            dialog.close();
            this.editBus = undefined;
            this.busForm.reset();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
    //#endregion Bus

    //#region BusStop
    private editBusStop?: BusStop;
    protected busStops: BusStop[] = [];
    protected busStopForm!: FormGroup<BusStopForm>;

    public openBusStopDialog = (dialog: IgxDialogComponent, cell?: CellType): void => {
        this.editBusStop = cell?.row.data;
        this.busStopForm.reset(this.editBusStop);
        this.dialogHeader = cell ? 'Редактирай' : 'Добави';
        dialog!.open();
    }
    public busStopSubmit = (dialog: IgxDialogComponent): void => {
        this._saveBusStop(this.dialogHeader === 'Добави', dialog);
    }
    private _saveBusStop = async (add: boolean, dialog: IgxDialogComponent) => {
        try {
            if (add) {
                this.editBusStop = await this.settingsService.busStopAdd(this.busStopForm.value);
            } else {
                this.editBusStop = await this.settingsService.busStopUpdate({ ...this.busStopForm.value, id: this.editBusStop?.id });
            }
            this.busStops = updateCollection(this.editBusStop, this.busStops);
            this.msgs.success(`Спирка ${this.editBusStop.name} е успешно ${add ? 'добавена' : 'редактирана'}.`);
            dialog.close();
            this.editBusStop = undefined;
            this.busStopForm.reset();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
    //#endregion BusStop

    //#region Transport schema
    private editTransportSchema?: TransportSchema;
    private editBusStopSchema?: BusStopSchema;
    private dragStop?: BusStopSchema;
    protected transportSchemas: TransportSchema[] = [];
    protected drivers: User[] = [];
    protected transportSchemaForm!: FormGroup<TransportSchemaForm>;
    protected busStopSchemaForm!: FormGroup<BusStopSchemaForm>;
    protected sortPipeTrigger = 0;

    public openTransportSchemaDialog = (e: Event, dialog: IgxDialogComponent, schema?: TransportSchema): void => {
        e.stopPropagation();
        this.editTransportSchema = schema;
        this.transportSchemaForm.reset(this.editTransportSchema);
        this.dialogHeader = schema ? 'Редактирай схема' : 'Добави схема';
        dialog.open();
    }
    public transportSchemaSubmit = async (dialog: IgxDialogComponent): Promise<void> => {
        try {
            const add = this.dialogHeader == 'Добави схема';
            if (add) {
                this.editTransportSchema = await this.settingsService.transportSchemaAdd(this.transportSchemaForm.value);
            } else {
                this.editTransportSchema = await this.settingsService.transportSchemaUpdate({ ...this.transportSchemaForm.value, id: this.editTransportSchema?.id });
            }

            this.transportSchemas = updateCollection(this.editTransportSchema, this.transportSchemas);
            this.msgs.success(`Транспортна схема ${this.editTransportSchema.name} е успешно ${add ? 'добавена' : 'редактирана'}.`);
            dialog.close();
            this.editTransportSchema = undefined;
            this.transportSchemaForm.reset();
            this.busStopForm.reset();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
    public openStopSchemaDialog = (e: Event, dialog: IgxDialogComponent, schema: TransportSchema, stop?: BusStopSchema): void => {
        if ((e.target as any).innerText === "delete") {
            return;
        }
        e.stopPropagation();
        this.editBusStopSchema = stop;
        if (this.editBusStopSchema) {
            this.editBusStopSchema.schema = schema;
        }
        this.busStopSchemaForm.reset({
            schema: schema?.id,
            busStop: this.editBusStopSchema?.busStop?.id,
            timeMorning: this.editBusStopSchema?.timeMorning,
            timeEvening: this.editBusStopSchema?.timeEvening,
        });
        this.dialogHeader = stop ? 'Редактирай спирка' : 'Добави спирка';
        dialog.open();
    }
    public busStopSchemaSubmit = async (dialog: IgxDialogComponent): Promise<void> => {
        try {
            const add = this.dialogHeader === 'Добави спирка';
            if (add) {
                this.editBusStopSchema = await this.settingsService.busStopSchemaAdd(this.busStopSchemaForm.value);
            } else {
                this.editBusStopSchema = await this.settingsService.busStopSchemaUpdate({ ...this.busStopSchemaForm.value, id: this.editBusStopSchema?.id });
            }

            const transportSchema = this.transportSchemas.find(ts => ts.id === this.editBusStopSchema?.schema.id)!;
            transportSchema.stops = updateCollection(this.editBusStopSchema, transportSchema.stops);
            this.msgs.success(`Спирка ${this.editBusStopSchema?.busStop?.name} е успешно ${add ? 'добавена' : 'редактирана'}.`);
            dialog.close();
            this.editBusStopSchema = undefined;
            this.busStopSchemaForm.reset();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
    public deleteStopSchemaDialog = (e: Event, dialog: IgxDialogComponent, stop: BusStopSchema): void => {
        e.stopPropagation();
        this.editBusStopSchema = stop;
        dialog.open();
    }
    public async deleteStop(dialog: IgxDialogComponent){
        try {
            this.editBusStopSchema = await this.settingsService.busStopSchemaDelete(this.editBusStopSchema!.id!);
            const transportSchema = this.transportSchemas.find(ts => ts.id === this.editBusStopSchema?.schema.id)!;
            transportSchema.stops = updateCollection(this.editBusStopSchema, transportSchema.stops);
            this.msgs.success(`Спирка ${this.editBusStopSchema?.busStop?.name} е успешно изтрита.`);
            dialog.close();
            this.editBusStopSchema = undefined;
        }
        catch (err) {
            this.msgs.error(err);
        }

    }
    public stopDragStart = (stop: BusStopSchema) => {
        this.dragStop = stop;
    }
    public stopDragGhostCreated = (dragRef: IgxListItemComponent) => {
        dragRef.element.style.visibility = 'hidden';
    }
    public stopEnter = (stop: BusStopSchema) => {
        if (stop.id === this.dragStop?.id) {
            return;
        }

        const oldTimeCreate = this.dragStop!.timeCreate;
        this.dragStop!.timeCreate = stop.timeCreate;
        stop.timeCreate = oldTimeCreate;
        this.sortPipeTrigger++;
    }
    public stopDragEnd = async (dragRef: IgxListItemComponent, schema: TransportSchema) => {
        dragRef.element.style.visibility = 'visible';
        try {
            this.settingsService.transportSchemaUpdate(schema);
            this.msgs.success(`Подредба на спирки в ${schema.name} е успешно обновена.`);
        } catch (err) {
            this.msgs.error(err);
        }
    }
    //#endregion Transport schema

    //#region Template
    private editEmailTemplate?: Template;
    protected templates: Template[] = [];
    protected contexts: MailContext[] = [];
    protected emailTemplateForm!: FormGroup<TemplateForm>;
    protected editor!: Editor;
    protected context?: MailContext;

    public parseTemplateContext = (context: string): string => {
        return this.contexts.find(c => c.id === context)?.readableName ?? '';
    }

    public contextChanged = (e: ISimpleComboSelectionChangingEventArgs): void => {
        this.context = this.contexts.find(c => c.id === e.newValue);
    }

    public variableClick = (e: IChipClickEventArgs): void => {
        const variable = this.context?.variables.find(v => v.id === e.owner.id);
        this.editor.commands.insertText(`$\{${variable!.id}\}`).exec();
    }

    public conditionClick = (e: IChipClickEventArgs): void => {
        const condition = this.context?.conditions.find(c => c.id === e.owner.id);
        let conditions = '';
        const names = ['първо условие', 'второ условие', 'трето условие', 'четвърто условие', 'пето условие', 'шесто условие', 'седмо условие', 'осмо условие', 'девето условие']
        if (condition?.count && condition.count > 0) {
            conditions = names.slice(0, condition.count).join('/');
        }
        this.editor.commands.insertText(`$(${condition!.id}:${conditions}:по подразбиране)`).exec();
    }

    public addTemplate = async (dialog: IgxDialogComponent) => {
        dialog.open();
        this.dialogHeader = 'Добави';
        this.emailTemplateForm.reset();
    }
    public editTemplate = async (cell: CellType, dialog: IgxDialogComponent) => {
        dialog.open();
        this.editEmailTemplate = cell.row.data;
        this.dialogHeader = 'Редактирай';
        this.emailTemplateForm.reset(cell.row.data);
        this.context = this.contexts.find(c => c.id === cell.row.data.context);
    }
    public submitTemplate = async (dialog: IgxDialogComponent) => {
        try {
            const add = this.dialogHeader === 'Добави';
            if (add) {
                this.editEmailTemplate = await this.settingsService.emailTemplateAdd(this.emailTemplateForm.value);
            } else {
                this.editEmailTemplate = await this.settingsService.emailTemplateUpdate({ ...this.emailTemplateForm.value, id: this.editEmailTemplate?.id });
            }

            this.templates = updateCollection(this.editEmailTemplate, this.templates);
            this.msgs.success(`Шаблонът ${this.editEmailTemplate?.name} е успешно ${add ? 'добавен' : 'редактиран'}.`);
            this.editEmailTemplate = undefined;
            this.emailTemplateForm.reset();
            dialog.close();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
    //#endregion Template

    //#region Payments
    private editPayment?: PaymentSource;
    protected paymentSources: PaymentSource[] = [];
    protected paymentForm!: FormGroup<PaymentSourceForm>;

    public openPaymentDialog = (dialog: IgxDialogComponent, cell?: CellType): void => {
        this.editPayment = cell?.row?.data;
        this.paymentForm.reset(this.editPayment);
        this.dialogHeader = cell ? 'Редактирай' : 'Добави';
        dialog!.open();
    }
    public paymentSubmit = (dialog: IgxDialogComponent): void => {
        this._savePayment(this.dialogHeader === 'Добави', dialog);
    }
    private _savePayment = async (add: boolean, dialog: IgxDialogComponent) => {
        try {
            if (add) {
                this.editPayment = await this.settingsService.paymentSourceAdd(this.paymentForm.value);
            } else {
                this.editPayment = await this.settingsService.paymentSourceUpdate({ ...this.paymentForm.value, id: this.editPayment?.id });
            }
            this.paymentSources = updateCollection(this.editPayment, this.paymentSources);
            this.msgs.success(`Тип плащане ${this.editPayment.name} е успешно ${add ? 'добавено' : 'редактирано'}.`);
            this.editPayment = undefined;
            this.paymentForm.reset();
            dialog.close();
        }
        catch (err) {
            this.msgs.error(err);
        }
    }
    //#endregion Payments

    //#region Empty forms
    private _emptySportForm = (): SportForm => {
        return {
            name: new FormControl()
        }
    }
    private _emptyLevelForm = (): LevelForm => {
        return {
            name: new FormControl(),
            description: new FormControl(),
            public: new FormControl(),
        }
    }
    private _emptyDefaultValuesForm = (): DefaultValueForm => {
        return {
            parentId: new FormControl(),
            ageRange: new FormControl(),
            capacity: new FormControl(),
            price: new FormControl(),
            packageDuration: new FormControl(),
            deposit: new FormControl(),
            depositDueDays: new FormControl(),
        }
    }
    private _emptyBusForm = (): BusForm => {
        return {
            registrationNumber: new FormControl(),
            color: new FormControl(),
            capacity: new FormControl(),
            driver: new FormControl(),
        }
    }
    private _emptyBusStopForm = (): BusStopForm => {
        return {
            name: new FormControl(),
            description: new FormControl()
        }
    }
    private _emptyTransportSchemaForm = (): TransportSchemaForm => {
        return {
            name: new FormControl(),
            description: new FormControl(),
        }
    }
    private _emptyBusStopSchemaForm = (): BusStopSchemaForm => {
        return {
            schema: new FormControl(),
            busStop: new FormControl(),
            timeMorning: new FormControl(),
            timeEvening: new FormControl(),
        }
    }
    private _emptyTemplateForm = (): TemplateForm => {
        return {
            context: new FormControl(),
            name: new FormControl(),
            text: new FormControl(),
            subject: new FormControl(),
        }
    }
    private _emptyPaymentForm = (): PaymentSourceForm => {
        return {
            name: new FormControl(),
        }
    }
    //#endregion Empty forms

    private load = async (): Promise<void> => {
        this.wait.show();
        this.emailTemplateForm = new FormGroup(this._emptyTemplateForm());
        this.defaultValuesForm = new FormGroup(this._emptyDefaultValuesForm());
        this.busForm = new FormGroup(this._emptyBusForm());
        this.sportForm = new FormGroup(this._emptySportForm());
        this.levelForm = new FormGroup(this._emptyLevelForm());
        this.busStopForm = new FormGroup(this._emptyBusStopForm());
        this.transportSchemaForm = new FormGroup(this._emptyTransportSchemaForm());
        this.busStopSchemaForm = new FormGroup(this._emptyBusStopSchemaForm());
        this.paymentForm = new FormGroup(this._emptyPaymentForm());

        const data = await this.settingsService.all();
        this.sports = data.sports;
        this.levels = data.levels;
        this.defaultValues = data.defaultValues;
        this.editGlobalDefaultValue = this.defaultValues.find(v => !v.parentId);
        this.buses = data.buses;
        this.drivers = data.users;
        this.busStops = data.busStops;
        this.transportSchemas = data.transportSchemas;
        this.templates = data.templates;
        this.contexts = data.contexts;
        this.paymentSources = data.payments;
        this.wait.hide();

        // TODO: move this to programs and set specific default value per program
        // this.sportDefaultValue = this.defaultValues.find(v => v.parentId && v.parentId === this.selectedSport?.id);
    }
}
